import axios, { AxiosResponse, InternalAxiosRequestConfig } from 'axios';

import { HTTP_STATUS, MSAL_CONFIG, SEARCH_API_CONFIG } from '@/constants';
import { useAuthStore } from '@/stores/auth';
import { useLoadingStore } from '@/stores/loading';
import type { CampaignButtonResponse } from '@/types/campaign';
import type {
  FeedEntryResponse,
  FeedResponse,
  TopNewsResponse,
} from '@/types/feed-entrys';
import type { HomepageResponse } from '@/types/homepage';
import type {
  ContentPage,
  FaqPageIdResponse,
  PagesResponse,
} from '@/types/pages';
import type { SearchResponse } from '@/types/search';
import type { StoriesResponse } from '@/types/stories';
import type { TickerResponse } from '@/types/ticker';

// Set default common headers individually
axios.defaults.headers.common['Cache-Control'] = 'no-cache';
axios.defaults.headers.common['Pragma'] = 'no-cache';
axios.defaults.headers.common['Expires'] = '0';
axios.defaults.headers.common['Accept'] = 'application/json';
axios.defaults.headers.common['Content-Type'] = 'application/json';

axios.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const loadingStore = useLoadingStore();

    if (config.showLoading !== false) {
      loadingStore.startLoading();
    }

    const authStore = useAuthStore();
    if (authStore.user) {
      try {
        // Always acquire a fresh ID token for API calls
        // MSAL will automatically refresh if needed (requires offline_access scope)
        const idToken = await authStore.acquireToken(
          MSAL_CONFIG.SCOPES.FULL.split(' '),
        );
       
        if (!config.headers) {
          config.headers = {} as any;
        }
        config.headers['X-Authorization'] = `Bearer ${idToken}`;
        // if in local development, add dev-authorization-login header
        // because the middleware can not validate the azure token there
        if (import.meta.env.MODE !== 'production') {
          config.headers['dev-authorization-login'] = `true`;
        }
        return config;
      } catch (error) {
        console.error('Failed to acquire token:', error);
        // If token acquisition fails, clear auth and redirect to login
        authStore.logout();
        window.location.href = '/auth';
        return Promise.reject(error);
      }
    }

    if (config.headers) {
      delete config.headers['X-Authorization'];
      delete config.headers['dev-authorization-login'];
    }
    return config;
  },
  (error) => {
    if (
      axios.isAxiosError(error) &&
      error.response?.status === HTTP_STATUS.UNAUTHORIZED
    ) {
      useLoadingStore().stopLoading();
      const authStore = useAuthStore();
      authStore.logout();
      window.location.href = '/auth';
    }
    return Promise.reject(error); // IMPORTANT: Return or throw error!
  },
);

axios.interceptors.response.use(
  (response) => {
    const loadingStore = useLoadingStore();
    if (response.config.showLoading !== false) {
      loadingStore.stopLoading();
    }
    return response;
  },
  (error) => {
    const loadingStore = useLoadingStore();
    if (error.config?.showLoading !== false) {
      loadingStore.stopLoading();
    }
    return Promise.reject(error);
  },
);

const API_BASE_URL =
  import.meta.env.MODE === 'production'
    ? 'https://app-api.uk-koeln.de/api'
    : 'https://api.uk-aktuell.sunzity.de/api';

type ApiResponse<T> = Promise<AxiosResponse<T>>;

export const getFeedEntries = (
  start: number,
  type: 'news' | 'hint',
): ApiResponse<FeedResponse<any>> =>
  axios.post(`${API_BASE_URL}/news-feed/entries`, { page: start, type: type });

export const getFeedEntryById = (
  id: string | number,
): ApiResponse<FeedEntryResponse<any>> =>
  axios.get(`${API_BASE_URL}/news-feed/entries/${id}`);

export const getTopNews = (): ApiResponse<TopNewsResponse> =>
  axios.get(`${API_BASE_URL}/news-feed/top-news`);

export const getEvents = (start: number): ApiResponse<FeedResponse<any>> =>
  axios.post(`${API_BASE_URL}/event/all`, { page: start });

export const getEventById = (
  id: string | number,
): ApiResponse<FeedEntryResponse<any>> =>
  axios.get(`${API_BASE_URL}/event/${id}`);

export const getPages = (): ApiResponse<PagesResponse> =>
  axios.get(`${API_BASE_URL}/page/all`);

export const getPageById = (id: string | number): ApiResponse<ContentPage> =>
  axios.get(`${API_BASE_URL}/page/${id}`);

export const getFaqPageId = (): ApiResponse<FaqPageIdResponse> =>
  axios.get(`${API_BASE_URL}/page/faqPageId`);

export const getPrivacyPageId = (): ApiResponse<FaqPageIdResponse> =>
  axios.get(`${API_BASE_URL}/page/privacyPageId`);

export const getClassifieds = (page: number): ApiResponse<FeedResponse<any>> =>
  axios.post(`${API_BASE_URL}/classified/all`, { page });

export const getAlertTicker = (): ApiResponse<TickerResponse> =>
  axios.get(`${API_BASE_URL}/homepage/ticker`);

export const getHomepageContent = (): ApiResponse<HomepageResponse> =>
  axios.get(`${API_BASE_URL}/homepage/content`);

export const getStories = (): ApiResponse<StoriesResponse> =>
  axios.get(`${API_BASE_URL}/stories`);

export const search = (
  q: string,
  start?: number,
  limit?: number,
  type?: string,
): ApiResponse<SearchResponse> => {
  let searchUrl = `${API_BASE_URL}/search?q=${encodeURIComponent(q)}`;
  searchUrl += '&page=' + (start ?? 1);
  searchUrl += '&limit=' + (limit ?? SEARCH_API_CONFIG.DEFAULT_LIMIT);
  if (type) {
    searchUrl += `&type=${encodeURIComponent(type)}`;
  }
  return axios.get(searchUrl);
};

export const getCampaignButton = (): ApiResponse<CampaignButtonResponse> =>
  axios.get(`${API_BASE_URL}/button/show`);
